import moment from 'moment';

/**
 * ТЗ 0080 — чистая логика экрана «Короткие ссылки»: словари бэкенда, фильтры
 * в адресе, тон статуса, разбор ошибок мутаций, вид перехода.
 *
 * Без React и без Apollo — всё здесь проверяется юнит-тестами
 * (`__tests__/shortLinkModel.test.ts`).
 */

/**
 * Вид ссылки (`ShortLinkType.kind`). `other` — служебная, фильтра нет.
 * ТЗ 0081: `sms` — ссылки из SMS студентам (ручные, рассылки, оплата,
 * доступ в LMS). ТЗ 0084: `email` — ссылки из шаблонных и системных писем
 * (письмо лиду, приглашение на подпись, рассылки SendGrid, робот
 * дофинансирования).
 */
export const SHORT_LINK_KINDS = [
  'manual',
  'contract',
  'sms',
  'email',
  'other',
] as const;
export type ShortLinkKind = typeof SHORT_LINK_KINDS[number];

/** Виды, по которым фильтрует сервер (`shortLinks(kind:)`). */
export const SHORT_LINK_KIND_FILTERS = [
  'manual',
  'contract',
  'sms',
  'email',
] as const;
export type ShortLinkKindFilter = typeof SHORT_LINK_KIND_FILTERS[number];

/** Состояние ссылки (`ShortLinkType.state`) — оно же фильтр `state:`. */
export const SHORT_LINK_STATES = ['live', 'revoked', 'expired'] as const;
export type ShortLinkState = typeof SHORT_LINK_STATES[number];

/**
 * Коды отказов `createShortLink` / `updateShortLink` / `revokeShortLink`
 * (`shortlinks/manual.py`). У каждого — перевод `shortLinks.errors.<CODE>`:
 * текст бэкенда только русский, а экран бывает и польским.
 */
export const SHORT_LINK_ERROR_CODES = [
  'SHORTLINK_DISABLED',
  'INVALID_URL',
  'TITLE_REQUIRED',
  'INVALID_EXPIRY',
  'NO_LEGAL_ENTITY',
  'FORBIDDEN',
  'NOT_FOUND',
  'TOO_LONG',
] as const;

/** Поля формы, к которым сервер привязывает ошибку (`customErrors.field`). */
export const SHORT_LINK_FORM_FIELDS = [
  'targetUrl',
  'title',
  'expiresAt',
  'channel',
  'tags',
] as const;
export type ShortLinkFormField = typeof SHORT_LINK_FORM_FIELDS[number];

/** Страница списка: столько же, сколько отдаёт сервер по умолчанию. */
export const SHORT_LINK_PAGE_SIZE = 50;

/** Страница выгрузки CSV — предел сервера (`MAX_PAGE`). */
export const SHORT_LINK_EXPORT_PAGE_SIZE = 100;

/** За сколько дней график переходов в карточке. */
export const SHORT_LINK_CHART_DAYS = 30;

/** Сколько последних переходов показывает карточка ссылки. */
export const SHORT_LINK_CLICKS_LIMIT = 50;

/**
 * Типы (каналы) ссылок из списка ERP (`shortlinks/manual.py::CHANNELS`).
 * Кроме них тип — любой свой текст: его показываем как есть.
 */
export const SHORT_LINK_CHANNELS = [
  'email',
  'sms',
  'social',
  'ads',
  'messenger',
  'website',
  'other',
] as const;
export type ShortLinkChannel = typeof SHORT_LINK_CHANNELS[number];

/** Фильтр «Переходы»: есть переходы людей / ни одного. */
export const SHORT_LINK_CLICKS_FILTERS = ['with', 'without'] as const;
export type ShortLinkClicksFilter = typeof SHORT_LINK_CLICKS_FILTERS[number];

/**
 * Порядок списка (`shortLinks(orderBy:)`). Первый — по умолчанию: его в
 * адрес не пишем.
 */
export const SHORT_LINK_SORTS = [
  'created_desc',
  'created_asc',
  'human_desc',
  'clicks_desc',
  'last_click_desc',
  'expires_asc',
  'title_asc',
] as const;
export type ShortLinkSort = typeof SHORT_LINK_SORTS[number];
export const DEFAULT_SHORT_LINK_SORT: ShortLinkSort = 'created_desc';

/**
 * Имена параметров фильтра в адресе. `q`, а не `search`: так же, как в
 * «Договорах», — ссылку коллеге читают одинаково во всех разделах.
 */
export const SHORT_LINK_FILTER_PARAMS = {
  search: 'q',
  kind: 'kind',
  state: 'state',
  mine: 'mine',
  channel: 'channel',
  tag: 'tag',
  author: 'author',
  from: 'from',
  to: 'to',
  clicks: 'clicks',
} as const;

/** Параметр адреса с порядком списка. */
export const SHORT_LINK_SORT_PARAM = 'sort';

/** Параметр адреса, в котором живёт открытая карточка ссылки. */
export const SHORT_LINK_CARD_PARAM = 'link';

export interface IShortLinkFilters {
  search: string;
  kind: ShortLinkKindFilter | '';
  state: ShortLinkState | '';
  mine: boolean;
  /** Тип: код из списка или свой текст (сервер сравнивает без регистра). */
  channel: string;
  /** Метка — точное совпадение. */
  tag: string;
  /** Автор — id сотрудника. */
  author: string;
  /** Создана с / по — `YYYY-MM-DD`, сутки по Варшаве, включительно. */
  from: string;
  to: string;
  clicks: ShortLinkClicksFilter | '';
}

export const EMPTY_SHORT_LINK_FILTERS: IShortLinkFilters = {
  search: '',
  kind: '',
  state: '',
  mine: false,
  channel: '',
  tag: '',
  author: '',
  from: '',
  to: '',
  clicks: '',
};

const isOneOf = <T extends string>(
  values: readonly T[],
  value: string | null,
): value is T =>
  value !== null && (values as readonly string[]).includes(value);

/** Дата фильтра: только настоящая дата `YYYY-MM-DD`, иначе пусто. */
const cleanDay = (value: string | null): string => {
  const day = (value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(day) &&
    moment(day, 'YYYY-MM-DD', true).isValid()
    ? day
    : '';
};

/** Пробелы по краям и повторные пробелы внутри — как чистит сервер. */
const squash = (value: string | null | undefined): string =>
  (value || '').split(/\s+/).filter(Boolean).join(' ');

/**
 * Фильтры из строки адреса (`location.search`).
 *
 * Незнакомое значение (`?state=deleted`, руками или из старой закладки) —
 * пусто, а не уходит на сервер: там оно молча не фильтровало бы ничего, а
 * чип на экране всё равно не горел бы — список и фильтры разошлись бы. Тип и
 * метка — свободный текст (свой тип, любая метка): их только чистим.
 */
export const parseShortLinkFilters = (search: string): IShortLinkFilters => {
  const params = new URLSearchParams(search);
  const get = (key: keyof typeof SHORT_LINK_FILTER_PARAMS) =>
    params.get(SHORT_LINK_FILTER_PARAMS[key]);
  const kind = get('kind');
  const state = get('state');
  const clicks = get('clicks');
  const author = (get('author') || '').trim();
  return {
    search: (get('search') || '').trim(),
    kind: isOneOf(SHORT_LINK_KIND_FILTERS, kind) ? kind : '',
    state: isOneOf(SHORT_LINK_STATES, state) ? state : '',
    mine: get('mine') === '1',
    channel: squash(get('channel')),
    tag: squash(get('tag')),
    author: /^\d+$/.test(author) ? author : '',
    from: cleanDay(get('from')),
    to: cleanDay(get('to')),
    clicks: isOneOf(SHORT_LINK_CLICKS_FILTERS, clicks) ? clicks : '',
  };
};

/**
 * Новая строка адреса: `patch` поверх фильтров из `search`. Прочие параметры
 * (`?link=<id>` — открытая карточка, `?sort=`) не трогаются; пустое значение
 * удаляет параметр, а не пишет `?kind=`.
 */
export const serializeShortLinkFilters = (
  search: string,
  patch: Partial<IShortLinkFilters>,
): string => {
  const params = new URLSearchParams(search);
  const next = { ...parseShortLinkFilters(search), ...patch };
  const values: Record<keyof IShortLinkFilters, string> = {
    search: next.search.trim(),
    kind: next.kind,
    state: next.state,
    mine: next.mine ? '1' : '',
    channel: squash(next.channel),
    tag: squash(next.tag),
    author: next.author.trim(),
    from: cleanDay(next.from),
    to: cleanDay(next.to),
    clicks: next.clicks,
  };
  (Object.keys(values) as Array<keyof IShortLinkFilters>).forEach((key) => {
    const param = SHORT_LINK_FILTER_PARAMS[key];
    if (values[key]) params.set(param, values[key]);
    else params.delete(param);
  });
  return params.toString();
};

export const hasShortLinkFilters = (filters: IShortLinkFilters): boolean =>
  Boolean(
    filters.search ||
      filters.kind ||
      filters.state ||
      filters.mine ||
      filters.channel ||
      filters.tag ||
      filters.author ||
      filters.from ||
      filters.to ||
      filters.clicks,
  );

/**
 * Переменные запроса: пустое — `null`, а не `""` / `false`. Так один и тот же
 * набор фильтров даёт одну и ту же запись кэша, как бы его ни собрали.
 */
export const toShortLinkQueryVariables = (filters: IShortLinkFilters) => ({
  search: filters.search.trim() || null,
  kind: filters.kind || null,
  state: filters.state || null,
  mine: filters.mine || null,
  channel: squash(filters.channel) || null,
  tag: squash(filters.tag) || null,
  createdById: filters.author || null,
  createdFrom: filters.from || null,
  createdTo: filters.to || null,
  clicks: filters.clicks || null,
});

/** Порядок из адреса; незнакомый или пустой — по умолчанию. */
export const parseShortLinkSort = (search: string): ShortLinkSort => {
  const value = new URLSearchParams(search).get(SHORT_LINK_SORT_PARAM);
  return isOneOf(SHORT_LINK_SORTS, value) ? value : DEFAULT_SHORT_LINK_SORT;
};

/** Строка адреса с порядком; порядок по умолчанию параметр удаляет. */
export const withShortLinkSort = (
  search: string,
  sort: ShortLinkSort,
): string => {
  const params = new URLSearchParams(search);
  if (sort === DEFAULT_SHORT_LINK_SORT) params.delete(SHORT_LINK_SORT_PARAM);
  else params.set(SHORT_LINK_SORT_PARAM, sort);
  return params.toString();
};

/** Колонки таблицы, по которым сортируют щелчком по заголовку. */
export type ShortLinkSortColumn =
  | 'title'
  | 'clicks'
  | 'lastClick'
  | 'expiry'
  | 'created';

const COLUMN_SORT: Record<
  Exclude<ShortLinkSortColumn, 'created'>,
  ShortLinkSort
> = {
  title: 'title_asc',
  clicks: 'human_desc',
  lastClick: 'last_click_desc',
  expiry: 'expires_asc',
};

/** Горит ли стрелка у колонки и куда она смотрит. */
export const shortLinkColumnSort = (
  column: ShortLinkSortColumn,
  sort: ShortLinkSort,
): 'asc' | 'desc' | null => {
  if (column === 'created') {
    if (sort === 'created_desc') return 'desc';
    if (sort === 'created_asc') return 'asc';
    return null;
  }
  if (sort !== COLUMN_SORT[column]) return null;
  return sort.endsWith('_asc') ? 'asc' : 'desc';
};

/**
 * Порядок после щелчка по заголовку. «Создана» переключает «новые / старые»;
 * у прочих колонок направление одно (по алфавиту, больше переходов, ближе
 * срок), и второй щелчок возвращает порядок по умолчанию.
 */
export const nextShortLinkSort = (
  column: ShortLinkSortColumn,
  sort: ShortLinkSort,
): ShortLinkSort => {
  if (column === 'created') {
    return sort === 'created_desc' ? 'created_asc' : 'created_desc';
  }
  const target = COLUMN_SORT[column];
  return sort === target ? DEFAULT_SHORT_LINK_SORT : target;
};

/** Известный код типа (без учёта регистра: сервер фильтрует так же). */
export const shortLinkChannelCode = (
  channel: string | null | undefined,
): ShortLinkChannel | null => {
  const value = (channel || '').trim().toLowerCase();
  return isOneOf(SHORT_LINK_CHANNELS, value) ? value : null;
};

/**
 * Подпись типа: код из списка — перевод, свой текст — как есть, пусто —
 * пусто. `translate` — `t` страницы: модель остаётся без i18next.
 */
export const shortLinkChannelLabel = (
  channel: string | null | undefined,
  translate: (key: string) => string,
): string => {
  const code = shortLinkChannelCode(channel);
  if (code) return translate(`shortLinks.channel.${code}`);
  return squash(channel);
};

/**
 * Перепроверка 0084: метки, которые ставит сервер ссылкам из SMS, — ключ
 * темы (`lead_sms`, `lead_dofin`…). Экран показывает их словами («SMS
 * лиду», «Лесенка «Анкета»»); фильтр по метке по-прежнему идёт кодом.
 */
export const SHORT_LINK_TAG_LABELS = [
  'lead_sms',
  'lead_reminder',
  'lead_dofin',
  'dofin_candidate_sms',
  'student_sms',
  'group_sms',
  'invoice_payment',
  'lms_access',
  'teacher_sms',
  // ТЗ 0084: подпись договора (лесенка) и ссылки из шаблонных и системных
  // писем; `auto` — адрес, вписанный прямо в текст, сервер сократил сам.
  'esign_signature',
  'lead_email',
  'esign_email',
  'lms_block_email',
  'dofin_email',
  'sendgrid_mailing',
  'auto',
] as const;

/** Подпись метки: служебный код темы — словами, свой текст — как есть. */
export const shortLinkTagLabel = (
  tag: string,
  translate: (key: string) => string,
): string =>
  (SHORT_LINK_TAG_LABELS as readonly string[]).includes(tag)
    ? translate(`shortLinks.tagLabels.${tag}`)
    : tag;

/**
 * Свои типы для выпадашек: всё из справочника юрлица, кроме кодов списка
 * (они и так в выпадашке), без повторов с точностью до регистра.
 */
export const customShortLinkChannels = (
  channels: ReadonlyArray<string>,
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  channels.forEach((raw) => {
    const value = squash(raw);
    if (!value || shortLinkChannelCode(value)) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
};

/** Сколько символов влезает в тип и в одну метку (длина колонки). */
export const SHORT_LINK_LABEL_MAX = 64;

/**
 * Добавить метки из набранного: запятая делит на несколько, пробелы
 * схлопываются, пустые и повторы (без учёта регистра) выпадают, порядок —
 * как ввёл человек. Так же чистит и сервер — поле ничего не обещает, чего
 * сервер не сохранит.
 */
export const addShortLinkTags = (
  current: ReadonlyArray<string>,
  typed: string,
): string[] => {
  const out = [...current];
  const seen = new Set(current.map((tag) => tag.toLowerCase()));
  typed.split(',').forEach((raw) => {
    const tag = squash(raw);
    if (!tag || seen.has(tag.toLowerCase())) return;
    seen.add(tag.toLowerCase());
    out.push(tag);
  });
  return out;
};

/** Убрать метку (без учёта регистра). */
export const removeShortLinkTag = (
  current: ReadonlyArray<string>,
  tag: string,
): string[] =>
  current.filter((item) => item.toLowerCase() !== tag.toLowerCase());

/**
 * Цель для превью в окне создания: как её сохранит сервер. Без схемы сервер
 * дописывает `https://` — превью показывает тот же домен. Не адрес — `null`.
 */
export const shortLinkTargetPreview = (
  raw: string,
): { url: string; host: string; path: string } | null => {
  const value = raw.trim();
  if (!value) return null;
  const url = value.includes('://') ? value : `https://${value}`;
  try {
    const parsed = new URL(url);
    if (!/^https?:$/i.test(parsed.protocol) || !parsed.hostname) return null;
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return { url, host: parsed.hostname, path: path === '/' ? '' : path };
  } catch {
    return null;
  }
};

/**
 * Колонки таблицы, которые можно спрятать («Название» — всегда). Последний
 * переход — второй строкой в «Переходах», дата создания — в «Создал»: так
 * таблица влезает в 1366 без прокрутки вбок.
 */
export const SHORT_LINK_COLUMNS = [
  'link',
  'target',
  'clicks',
  'expiry',
  'status',
  'createdBy',
] as const;
export type ShortLinkColumn = typeof SHORT_LINK_COLUMNS[number];

/** Ключ `localStorage` со СКРЫТЫМИ колонками: новая колонка видна сразу. */
export const SHORT_LINK_COLUMNS_STORAGE_KEY = 'erp.shortLinks.hiddenColumns';

/**
 * Скрытые колонки из `localStorage`: мусор, незнакомые имена и чужой формат —
 * пусто (все видны), а не падение страницы.
 */
export const parseHiddenShortLinkColumns = (
  raw: string | null,
): ShortLinkColumn[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return SHORT_LINK_COLUMNS.filter((column) => parsed.includes(column));
  } catch {
    return [];
  }
};

/** Отбирает строки, которые текущий сотрудник может погасить. */
export const revocableShortLinkIds = (
  rows: ReadonlyArray<{ id: string; canRevoke: boolean }>,
): string[] => rows.filter((row) => row.canRevoke).map((row) => row.id);

/** Сколько id уходит в один вызов `revokeShortLinks` (предел сервера). */
export const SHORT_LINK_BULK_LIMIT = 100;

/** Разбить на пачки по `size`. */
export const chunkShortLinkIds = (
  ids: ReadonlyArray<string>,
  size = SHORT_LINK_BULK_LIMIT,
): string[][] => {
  const out: string[][] = [];
  for (let at = 0; at < ids.length; at += size) {
    out.push(ids.slice(at, at + size));
  }
  return out;
};

/** Строка адреса с открытой (`id`) или закрытой (`null`) карточкой. */
export const withShortLinkCard = (
  search: string,
  id: string | null,
): string => {
  const params = new URLSearchParams(search);
  if (id) params.set(SHORT_LINK_CARD_PARAM, id);
  else params.delete(SHORT_LINK_CARD_PARAM);
  return params.toString();
};

export type ShortLinkTone = 'ok' | 'neutral' | 'warn';

/**
 * Тон плашки статуса: действует — зелёный, погашена — нейтральный (решение
 * человека, не беда), истекла — янтарный (ссылка, возможно, ещё разослана и
 * уже не открывается). Незнакомое состояние — нейтральный.
 */
export const shortLinkStateTone = (state: string): ShortLinkTone => {
  if (state === 'live') return 'ok';
  if (state === 'expired') return 'warn';
  return 'neutral';
};

export const isShortLinkState = (value: string): value is ShortLinkState =>
  isOneOf(SHORT_LINK_STATES, value);

export const isShortLinkKind = (value: string): value is ShortLinkKind =>
  isOneOf(SHORT_LINK_KINDS, value);

export interface IShortLinkError {
  field: string;
  code: string;
  message: string;
}

type RawErrors =
  | Array<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

export const toShortLinkErrors = (raw: RawErrors): IShortLinkError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));

export type ShortLinkErrorsByField = Record<
  ShortLinkFormField | 'form',
  IShortLinkError[]
>;

/**
 * Разложить ошибки по полям формы. Ошибка поля — под этим полем; всё прочее
 * (`link`, пустое поле, незнакомое имя — `SHORTLINK_DISABLED`, `FORBIDDEN`,
 * отказ сети) — общей строкой внизу окна. Ничего не теряется: сумма длин
 * групп равна числу ошибок.
 */
export const groupShortLinkErrors = (
  errors: IShortLinkError[],
): ShortLinkErrorsByField => {
  const grouped: ShortLinkErrorsByField = {
    targetUrl: [],
    title: [],
    expiresAt: [],
    channel: [],
    tags: [],
    form: [],
  };
  errors.forEach((error) => {
    const field = isOneOf(SHORT_LINK_FORM_FIELDS, error.field)
      ? error.field
      : 'form';
    grouped[field].push(error);
  });
  return grouped;
};

/**
 * Ключ перевода ошибки: известный код — `shortLinks.errors.<CODE>`, иначе
 * `null`, и экран показывает текст сервера (или общий отказ, если пуст).
 */
export const shortLinkErrorKey = (code: string): string | null =>
  isOneOf(SHORT_LINK_ERROR_CODES, code) ? `shortLinks.errors.${code}` : null;

export type ShortLinkClickKind = 'human' | 'repeat' | 'bot' | 'unconfirmed';

export interface IShortLinkClickFlags {
  human: boolean;
  counted: boolean;
  isBot: boolean;
}

/**
 * Вид перехода в журнале карточки.
 *
 * * `human` — браузер подтвердил переход, и он засчитан;
 * * `repeat` — подтверждён, но это повтор с того же адреса (не засчитан);
 * * `bot` — известный робот по User-Agent;
 * * `unconfirmed` — GET без подтверждения: превью мессенджера, проверка
 *   антивирусом, человек, закрывший страницу раньше, — в «людей» не входит.
 *
 * Подтверждение браузера сильнее флага робота: засчитанный человек — человек.
 */
export const shortLinkClickKind = ({
  human,
  counted,
  isBot,
}: IShortLinkClickFlags): ShortLinkClickKind => {
  if (human) return counted ? 'human' : 'repeat';
  if (isBot) return 'bot';
  return 'unconfirmed';
};

export const SHORT_LINK_CLICK_KINDS: ShortLinkClickKind[] = [
  'human',
  'repeat',
  'bot',
  'unconfirmed',
];

/**
 * Цель ссылки для узкой колонки: домен отдельно, путь — укороченным.
 * `https://learnit.com.pl/kursy/python?utm=x` → `learnit.com.pl` +
 * `/kursy/python?utm=x`. Путь длиннее `max` обрезается с многоточием в конце:
 * хвост рассылочной ссылки (utm-метки) — самое неинтересное в ней. Полный
 * адрес — в подсказке и в карточке.
 */
export const splitShortLinkTarget = (
  targetUrl: string | null | undefined,
  host: string | null | undefined,
  max = 40,
): { host: string; path: string } => {
  if (!targetUrl) return { host: host || '', path: '' };
  let parsedHost = host || '';
  let path = '';
  try {
    const url = new URL(targetUrl);
    parsedHost = host || url.hostname;
    path = `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return { host: parsedHost, path: '' };
  }
  if (path === '/') path = '';
  if (path.length > max) path = `${path.slice(0, Math.max(1, max - 1))}…`;
  return { host: parsedHost, path };
};

/**
 * Короткая ссылка для узкой колонки — без схемы: `https://tms.pl/s/AB12`
 * → `tms.pl/s/AB12`. Копируется и открывается всегда полный адрес.
 */
export const shortLinkDisplayUrl = (url: string | null | undefined): string =>
  (url || '').replace(/^https?:\/\//i, '');

/** Формат `<input type="datetime-local">`. */
export const SHORT_LINK_EXPIRY_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

/**
 * Срок из поля `datetime-local` («2026-10-01T18:00», местное время) — в ISO со
 * смещением («2026-10-01T18:00:00+02:00»), который сервер разбирает без
 * догадок о часовом поясе. Пусто или мусор — `null`.
 */
export const shortLinkExpiryToIso = (local: string): string | null => {
  if (!local) return null;
  const parsed = moment(local, SHORT_LINK_EXPIRY_INPUT_FORMAT, true);
  return parsed.isValid() ? parsed.format() : null;
};

/** Обратно: ISO с сервера — значение для поля `datetime-local`. */
export const shortLinkExpiryToInput = (
  iso: string | null | undefined,
): string => {
  if (!iso) return '';
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format(SHORT_LINK_EXPIRY_INPUT_FORMAT) : '';
};
